import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, ZoomIn, ZoomOut, ImageIcon, Upload } from 'lucide-react';

/** Small thumbnail; click opens a full-screen viewer with zoom. */
export function Thumb({ src, size = 40, alt = '', onClick }) {
  const [open, setOpen] = useState(false);
  if (!src) {
    return (
      <div style={{ width: size, height: size, borderRadius: 10, background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <ImageIcon size={size * 0.4} style={{ color: 'var(--text-3)' }} />
      </div>
    );
  }
  return (
    <>
      <img
        src={src} alt={alt}
        onClick={e => { e.stopPropagation(); onClick ? onClick() : setOpen(true); }}
        style={{ width: size, height: size, objectFit: 'cover', borderRadius: 10, cursor: 'zoom-in', border: '1px solid var(--border)', flexShrink: 0 }}
      />
      {open && <ImageViewer src={src} alt={alt} onClose={() => setOpen(false)} />}
    </>
  );
}

export function ImageViewer({ src, alt = '', onClose }) {
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const btn = { background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)', color: '#fff', borderRadius: 10, padding: 8, cursor: 'pointer', display: 'flex' };
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 3000, background: 'rgba(0,0,0,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'auto', backdropFilter: 'blur(8px)' }}>
      <div style={{ position: 'fixed', top: 16, right: 16, display: 'flex', gap: 8, zIndex: 3001 }} onClick={e => e.stopPropagation()}>
        <button style={btn} onClick={() => setScale(s => Math.max(0.5, s - 0.5))}><ZoomOut size={18} /></button>
        <button style={btn} onClick={() => setScale(s => Math.min(5, s + 0.5))}><ZoomIn size={18} /></button>
        <button style={btn} onClick={onClose}><X size={18} /></button>
      </div>
      <img
        src={src} alt={alt}
        onClick={e => { e.stopPropagation(); setScale(s => (s === 1 ? 2.5 : 1)); }}
        style={{ maxWidth: scale === 1 ? '92vw' : 'none', maxHeight: scale === 1 ? '88vh' : 'none', transform: `scale(${scale})`, transformOrigin: 'center', transition: 'transform 0.2s', cursor: scale === 1 ? 'zoom-in' : 'zoom-out', borderRadius: 12 }}
      />
    </div>
  );
}

/** Image picker used inside edit modals. `value` is a URL (existing image) or a File (pending upload). */
export function ImagePicker({ value, onChange, onRemove, label }) {
  const { t } = useTranslation();
  const preview = value instanceof File ? URL.createObjectURL(value) : value;
  return (
    <div className="form-group">
      {label && <label className="form-label">{label}</label>}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <Thumb src={preview} size={64} />
        <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}>
          <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => e.target.files?.[0] && onChange(e.target.files[0])} />
          <Upload size={13} /> {preview ? t('change_image') : t('upload_image')}
        </label>
        {preview && onRemove && <button type="button" className="btn btn-ghost btn-sm" onClick={onRemove}><X size={13} /></button>}
      </div>
    </div>
  );
}
